import {
  FETCH_SUBSCRIPTION_PLANS,
  FETCH_SUBSCRIPTION_PLANS_SUCCESS,
  FETCH_SUBSCRIPTION_PLANS_FAILED,
  FETCH_USER_SUBSCRIPTION,
  FETCH_USER_SUBSCRIPTION_SUCCESS,
  FETCH_USER_SUBSCRIPTION_FAILED,
  CREATE_SUBSCRIPTION,
  CREATE_SUBSCRIPTION_SUCCESS,
  CREATE_SUBSCRIPTION_FAILED,
  CHECK_TRIP_USAGE,
  CHECK_TRIP_USAGE_SUCCESS,
  CHECK_TRIP_USAGE_FAILED,
  FETCH_SUBSCRIPTION_STATS,
  FETCH_SUBSCRIPTION_STATS_SUCCESS,
  FETCH_SUBSCRIPTION_STATS_FAILED,
  VERIFY_OFFLINE_PAYMENT,
  VERIFY_OFFLINE_PAYMENT_SUCCESS,
  VERIFY_OFFLINE_PAYMENT_FAILED,
  CLEAR_SUBSCRIPTION_ERROR
} from "../store/types";
import { RequestPushMsg } from '../other/NotificationFunctions';
import { fetchUser } from './authactions';
import { firebase } from '../config/configureFirebase';
import store from '../store/store';
import { Platform } from 'react-native';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';

// Helper function to get API base URL
const getApiBaseUrl = () => {
  const { config } = firebase;
  const settings = store.getState().settingsdata.settings;

  // Check if running in React Native (mobile app)
  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    return `https://us-central1-${config.projectId}.cloudfunctions.net`;
  }

  // For web app
  return window && window.location && settings.CompanyWebsite === window.location.origin
    ? window.location.origin
    : `https://${config.projectId}.web.app`;
};

export const fetchSubscriptionPlans = (userType = null, language = 'en') => async (dispatch, getState) => {
  dispatch({
    type: FETCH_SUBSCRIPTION_PLANS,
    payload: null,
  });
  try {
    const { database } = firebase;
    if (!database) {
      throw new Error('Firebase database not initialized');
    }

    // Get plans directly from Firebase
    const plansRef = ref(database, 'subscription_plans');
    const snapshot = await get(plansRef);
    let allPlans = snapshot.val() || {};

    // Filter by userType if provided
    if (userType) {
      const filteredPlans = {};
      Object.keys(allPlans).forEach(key => {
        const plan = allPlans[key];
        if (plan.userType === userType && plan.isActive !== false) {
          filteredPlans[key] = plan;
        }
      });
      allPlans = filteredPlans;
    } else {
      // Filter out inactive plans if no userType specified
      const filteredPlans = {};
      Object.keys(allPlans).forEach(key => {
        const plan = allPlans[key];
        if (plan.isActive !== false) {
          filteredPlans[key] = plan;
        }
      });
      allPlans = filteredPlans;
    }

    // Process translations based on language
    const processedPlans = {};
    Object.keys(allPlans).forEach(key => {
      const plan = allPlans[key];
      const translation = plan.translations && plan.translations[language] 
        ? plan.translations[language] 
        : (plan.translations && plan.translations['en'] ? plan.translations['en'] : null);
      
      processedPlans[key] = {
        ...plan,
        key: key,
        name: translation?.name || plan.name || key,
        description: translation?.description || plan.description || '',
        features: translation?.features || plan.features || [],
      };
    });

    dispatch({
      type: FETCH_SUBSCRIPTION_PLANS_SUCCESS,
      payload: { plans: processedPlans, language: language },
    });
  } catch (error) {
    console.error('Error fetching subscription plans from Firebase:', error);
    dispatch({
      type: FETCH_SUBSCRIPTION_PLANS_FAILED,
      payload: error.message || 'Failed to fetch subscription plans from Firebase',
    });
  }
};

export const fetchUserSubscription = (userId) => async (dispatch) => {
  dispatch({
    type: FETCH_USER_SUBSCRIPTION,
    payload: null,
  });
  try {
    if (!userId) {
      dispatch({
        type: FETCH_USER_SUBSCRIPTION_FAILED,
        payload: 'User ID is required',
      });
      return;
    }

    const { database } = firebase;
    if (!database) {
      throw new Error('Firebase database not initialized');
    }

    // Get subscription directly from Firebase
    const subscriptionRef = ref(database, `subscriptions/${userId}`);
    const snapshot = await get(subscriptionRef);
    const subscription = snapshot.val();

    if (!subscription) {
      dispatch({
        type: FETCH_USER_SUBSCRIPTION_FAILED,
        payload: 'Subscription not found',
      });
      return;
    }

    // Check if subscription is expired
    const currentTime = Date.now();
    const endDate = subscription.endDate;

    if (endDate && currentTime > endDate && subscription.status === 'active') {
      // Update status to expired
      subscription.status = 'expired';
    }

    dispatch({
      type: FETCH_USER_SUBSCRIPTION_SUCCESS,
      payload: subscription,
    });
  } catch (error) {
    console.error('Error fetching user subscription from Firebase:', error);
    dispatch({
      type: FETCH_USER_SUBSCRIPTION_FAILED,
      payload: error.message || 'Failed to fetch subscription from Firebase',
    });
  }
};

export const createSubscription = (subscriptionData) => async (dispatch) => {
  dispatch({
    type: CREATE_SUBSCRIPTION,
    payload: null,
  });
  try {
    const response = await fetch(`${getApiBaseUrl()}/subscription-createSubscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData)
    });
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: CREATE_SUBSCRIPTION_SUCCESS,
        payload: data.subscription,
      });

      // Refresh user data to get updated subscription status
      if (subscriptionData.userId) {
        dispatch(fetchUser());
      }

      return { success: true, subscription: data.subscription, message: data.message };
    } else {
      dispatch({
        type: CREATE_SUBSCRIPTION_FAILED,
        payload: data.error || 'Failed to create subscription',
      });
      return { success: false, error: data.error };
    }
  } catch (error) {
    dispatch({
      type: CREATE_SUBSCRIPTION_FAILED,
      payload: error.message,
    });
    return { success: false, error: error.message };
  }
};

export const checkTripUsage = (userId, action = 'check') => async (dispatch) => {
  dispatch({
    type: CHECK_TRIP_USAGE,
    payload: null,
  });
  try {
    const response = await fetch(`${getApiBaseUrl()}/subscription-checkTripUsage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, action })
    });
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: CHECK_TRIP_USAGE_SUCCESS,
        payload: data,
      });

      // If action was increment and limit reached, show notification
      if (action === 'increment' && data.limitReached) {
        RequestPushMsg(
          userId,
          {
            title: 'Subscription Limit Reached',
            msg: 'Your monthly trip limit has been reached. Please upgrade your plan.',
            channelId: 'general',
            sound: Platform.OS === 'android' ? 'general' : 'general.mp3' // Correct format for each platform
          }
        );
      }

      return data;
    } else {
      dispatch({
        type: CHECK_TRIP_USAGE_FAILED,
        payload: data.error || 'Failed to check trip usage',
      });
      return data;
    }
  } catch (error) {
    dispatch({
      type: CHECK_TRIP_USAGE_FAILED,
      payload: error.message,
    });
    return { success: false, error: error.message };
  }
};

export const fetchSubscriptionStats = () => async (dispatch) => {
  dispatch({
    type: FETCH_SUBSCRIPTION_STATS,
    payload: null,
  });
  try {
    const response = await fetch(`${getApiBaseUrl()}/subscription-getSubscriptionStats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: FETCH_SUBSCRIPTION_STATS_SUCCESS,
        payload: data.stats,
      });
    } else {
      dispatch({
        type: FETCH_SUBSCRIPTION_STATS_FAILED,
        payload: data.error || 'Failed to fetch subscription statistics',
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_SUBSCRIPTION_STATS_FAILED,
      payload: error.message,
    });
  }
};

export const verifyOfflinePayment = (userId, approved, adminNote) => async (dispatch) => {
  dispatch({
    type: VERIFY_OFFLINE_PAYMENT,
    payload: null,
  });
  try {
    const response = await fetch(`${getApiBaseUrl()}/subscription-verifyOfflinePayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, approved, adminNote })
    });
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: VERIFY_OFFLINE_PAYMENT_SUCCESS,
        payload: data.message,
      });

      // Send notification to user
      const notificationTitle = approved ? 'Subscription Activated' : 'Subscription Rejected';
      const notificationBody = approved
        ? 'Your subscription has been activated successfully!'
        : 'Your subscription payment was rejected. Please contact support.';

      RequestPushMsg(userId, {
        title: notificationTitle,
        msg: notificationBody,
        channelId: 'general',
        sound: Platform.OS === 'android' ? 'general' : 'general.mp3' // Correct format for each platform
      });

      return { success: true, message: data.message };
    } else {
      dispatch({
        type: VERIFY_OFFLINE_PAYMENT_FAILED,
        payload: data.error || 'Failed to verify payment',
      });
      return { success: false, error: data.error };
    }
  } catch (error) {
    dispatch({
      type: VERIFY_OFFLINE_PAYMENT_FAILED,
      payload: error.message,
    });
    return { success: false, error: error.message };
  }
};

export const clearSubscriptionError = () => (dispatch) => {
  dispatch({
    type: CLEAR_SUBSCRIPTION_ERROR,
    payload: null,
  });
};

// Helper function to generate subscription order ID for payment gateways
export const generateSubscriptionOrderId = (userId, planId) => {
  return `subscription-${userId}-${planId}-${Date.now()}`;
};

// Initialize subscription plans (for admin) 
// Note: Backend function is commented out to prevent accidental data initialization
// This frontend function will show appropriate message if backend is unavailable
export const initializeSubscriptionPlans = (language = 'en') => async (dispatch) => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/subscription-initSubscriptionPlans`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language })
    });
    const data = await response.json();

    if (data.success) {
      // Refresh plans after initialization with the specified language
      dispatch(fetchSubscriptionPlans(null, language));
      return { success: true, message: data.message, language: data.language };
    } else {
      return { success: false, error: data.error };
    }
  } catch (error) {
    return { success: false, error: 'Backend initialization function is disabled for safety' };
  }
}; 